import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MapPin,
  Home,
  Building2,
  LandPlot,
  Settings,
  LogOut,
  X,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';

const brokerTypeStyles = {
  haus: {
    icon: Home,
    iconClassName: 'text-[#0f9d94]',
    bubbleClassName: 'bg-[#0f9d94]/20',
    label: 'Häuser',
    valueKey: 'houses',
  },
  wohnung: {
    icon: Building2,
    iconClassName: 'text-[#8b1e1e]',
    bubbleClassName: 'bg-[#8b1e1e]/20',
    label: 'Wohnungen',
    valueKey: 'apartments',
  },
  grundstueck: {
    icon: LandPlot,
    iconClassName: 'text-[#8b5a2b]',
    bubbleClassName: 'bg-[#8b5a2b]/20',
    label: 'Grundstücke',
    valueKey: 'properties',
  },
};

export const Sidebar = ({ user, stats, brokerList, activeBrokers, onBrokerToggle, onSelectAllBrokers, onLogout, onClose }) => {
  const navigate = useNavigate();
  const hasBrokerFilter = activeBrokers.length > 0;

  return (
    <motion.aside
      initial={{ x: -280 }}
      animate={{ x: 0 }}
      exit={{ x: -280 }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="fixed inset-y-0 left-0 z-30 flex h-full w-[20rem] max-w-[calc(100vw-1rem)] flex-col bg-slate-900 text-white shadow-2xl lg:static lg:w-72 lg:max-w-none"
      data-testid="sidebar"
    >
      <div className="absolute inset-0 sidebar-texture pointer-events-none" />

      <button
        type="button"
        onClick={onClose}
        className="absolute right-3 top-3 z-[60] rounded-full bg-slate-950/85 p-2 text-white shadow-lg transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-white/60 lg:hidden"
        data-testid="close-sidebar-btn"
        aria-label="Sidebar schließen"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div className="p-5 lg:p-6">
          <div className="mb-2 flex items-center gap-3 pr-12 lg:pr-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#b48d00]">
              <MapPin className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
                M&R Properties
              </h1>
              <p className="text-xs text-stone-400">Immobilienübersicht</p>
            </div>
          </div>
        </div>

        <Separator className="bg-slate-800" />

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="space-y-6 p-4 lg:p-6">
            <section>
              <p className="overline mb-3 text-stone-400">Makler auswählen</p>
              <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950/60">
                <label className="flex cursor-pointer items-center gap-3 border-b border-slate-800 px-4 py-3 transition-colors hover:bg-slate-800/60">
                  <Checkbox
                    checked={!hasBrokerFilter}
                    onCheckedChange={() => onSelectAllBrokers()}
                    className="border-slate-600 data-[state=checked]:border-[#b48d00] data-[state=checked]:bg-[#b48d00]"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">Alle Makler</p>
                    <p className="text-xs text-stone-400">Zeigt den gesamten Bestand</p>
                  </div>
                </label>

                <div className="max-h-[32vh] overflow-y-auto lg:max-h-56">
                  {brokerList.map((broker) => {
                    const isChecked = activeBrokers.includes(broker);
                    return (
                      <label
                        key={broker}
                        className="flex cursor-pointer items-center gap-3 border-b border-slate-800 px-4 py-3 transition-colors hover:bg-slate-800/60 last:border-b-0"
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => onBrokerToggle(broker)}
                          className="border-slate-600 data-[state=checked]:border-[#b48d00] data-[state=checked]:bg-[#b48d00]"
                        />
                        <span className="truncate text-sm text-white">{broker}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {hasBrokerFilter && (
                <p className="mt-2 break-words text-xs text-[#b48d00]">
                  Aktiv: {activeBrokers.join(', ')}
                </p>
              )}
            </section>

            <section>
              <p className="overline mb-3 text-stone-400">Übersicht</p>

              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-lg bg-slate-800/50 p-3 transition-colors hover:bg-slate-800">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#b48d00]/20">
                      <MapPin className="h-4 w-4 text-[#b48d00]" />
                    </div>
                    <span className="text-sm">Gesamt</span>
                  </div>
                  <span className="font-bold text-[#b48d00]">{stats.total}</span>
                </div>

                <div className="flex items-center justify-between rounded-lg bg-slate-800/50 p-3 transition-colors hover:bg-slate-800">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/20">
                      <MapPin className="h-4 w-4 text-emerald-400" />
                    </div>
                    <span className="text-sm">Aktiv</span>
                  </div>
                  <span className="font-bold text-emerald-400">{stats.active_total ?? 0}</span>
                </div>

                <div className="flex items-center justify-between rounded-lg bg-slate-800/50 p-3 transition-colors hover:bg-slate-800">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-500/20">
                      <MapPin className="h-4 w-4 text-slate-300" />
                    </div>
                    <span className="text-sm">Historisch</span>
                  </div>
                  <span className="font-bold text-slate-300">{stats.historical_total ?? stats.inactive_total ?? 0}</span>
                </div>

                {Object.entries(brokerTypeStyles).map(([type, config]) => {
                  const Icon = config.icon;
                  return (
                    <div key={type} className="flex items-center justify-between rounded-lg bg-slate-800/50 p-3 transition-colors hover:bg-slate-800">
                      <div className="flex items-center gap-3">
                        <div className={`flex h-8 w-8 items-center justify-center rounded-full ${config.bubbleClassName}`}>
                          <Icon className={`h-4 w-4 ${config.iconClassName}`} />
                        </div>
                        <span className="text-sm">{config.label}</span>
                      </div>
                      <span className={`font-bold ${config.iconClassName}`}>{stats[config.valueKey] ?? 0}</span>
                    </div>
                  );
                })}
              </div>
            </section>

            {user?.role === 'admin' && (
              <section>
                <Button
                  data-testid="admin-panel-btn"
                  variant="ghost"
                  className="w-full justify-between text-white hover:bg-slate-800 hover:text-white"
                  onClick={() => navigate('/admin')}
                >
                  <div className="flex items-center gap-3">
                    <Settings className="h-4 w-4" />
                    <span>Admin-Bereich</span>
                  </div>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </section>
            )}
          </div>
        </div>

        <Separator className="bg-slate-800" />

        <div className="p-4 lg:p-6">
          <div className="mb-4 flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-slate-700 text-white">
                {user?.name?.charAt(0) || 'M'}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{user?.name || 'Makler'}</p>
              <p className="truncate text-xs text-stone-400">{user?.email}</p>
            </div>
          </div>

          <Button
            data-testid="sidebar-logout-btn"
            variant="ghost"
            className="w-full justify-start text-stone-400 hover:bg-slate-800 hover:text-white"
            onClick={onLogout}
          >
            <LogOut className="mr-3 h-4 w-4" />
            Abmelden
          </Button>
        </div>
      </div>
    </motion.aside>
  );
};

export default Sidebar;
